import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Facebook, Instagram, Upload, Edit2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import BaseEditProfile, { EditSection, EditProfileImage } from '../BaseEditProfile';

// Constantes para URLs de Cloudflare
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';
const CLOUDFLARE_BANNERS_URL = 'https://pub-cdaec3d9feef44cf8950eee9a93266e4.r2.dev';

// Función mejorada para procesar imágenes de perfil
const getProfileImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /profiles/
  if (imageUrl.includes('/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_PROFILES_URL}/${imageUrl}`;
};

// Función mejorada para procesar banners
const getBannerImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /banners/
  if (imageUrl.includes('/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_BANNERS_URL}/${imageUrl}`;
};

const EditSellerProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [profileData, setProfileData] = useState({
    nombre: '',
    slogan: '',
    experiencia: '',
    sobre_nosotros: '',
    facebook: '',
    instagram: '',
    ubicacion: ''
  });

  // Estados para imágenes
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // Cargar datos del perfil
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Primero intentamos obtener el perfil más detallado (puede contener más campos)
        let detailedDataResponse;
        try {
          detailedDataResponse = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/perfil-detallado', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        } catch (err) {
          console.log('No se pudo obtener el perfil detallado, intentando con el perfil básico');
        }
        
        // Si el perfil detallado no existe o falla, usamos el endpoint básico
        const response = detailedDataResponse?.ok 
          ? detailedDataResponse
          : await fetch('https://backend-production-1786.up.railway.app/api/vendedores/perfil', {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

        if (!response.ok) throw new Error('Error al cargar el perfil');

        const data = await response.json();
        console.log('Datos de perfil recibidos:', data);
        
        // Crear un objeto con los campos que necesitamos
        setProfileData({
          nombre: data.nombre || '',
          slogan: data.slogan || '',
          experiencia: data.experiencia || '',
          sobre_nosotros: data.sobre_nosotros || '', // Intentar ambos campos
          facebook: data.facebook || '',
          instagram: data.instagram || '',
          ubicacion: data.ubicacion || '',
          whatsapp: data.whatsapp || ''
        });
        
        // Procesar URLs de imágenes correctamente usando las funciones mejoradas
        if (data.banner_image) {
          const bannerUrl = getBannerImageUrl(data.banner_image);
          console.log('URL de banner procesada:', bannerUrl);
          setBannerPreview(bannerUrl);
        }
  
        if (data.foto_perfil) {
          const profileUrl = getProfileImageUrl(data.foto_perfil);
          console.log('URL de imagen de perfil procesada:', profileUrl);
          setProfileImagePreview(profileUrl);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Manejadores de cambios
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  // Guardar cambios
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validaciones básicas
      if (!profileData.nombre?.trim()) {
        setError('El nombre es requerido');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se encontró el token de autenticación');
      }

      const formData = new FormData();

      // Lista de campos permitidos para actualizar
      const allowedFields = [
        'nombre',
        'slogan',
        'experiencia',
        'sobre_nosotros',
        'sobre_mi',
        'facebook',
        'instagram',
        'ubicacion',
        'whatsapp'
      ];

      // Añadir solo los campos permitidos que tengan valor
      allowedFields.forEach(field => {
        if (profileData[field] !== null && profileData[field] !== undefined && profileData[field] !== '') {
          formData.append(field, profileData[field]);
        }
      });

      // Añadir archivos solo si han sido modificados
      if (bannerFile) {
        formData.append('banner', bannerFile);
      }
      
      if (profileImageFile) {
        formData.append('profile_image', profileImageFile);
      }

      console.log('Datos a enviar:', Object.fromEntries(formData));

      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/perfil', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar los cambios');
      }

      const responseData = await response.json();
      console.log('Respuesta del servidor:', responseData);

      setHasUnsavedChanges(false);
      navigate('/mi_perfil');
      
    } catch (error) {
      console.error('Error al guardar:', error);
      setError(error.message || 'Error al guardar los cambios. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseEditProfile
      loading={loading}
      error={error}
      bannerPreview={bannerPreview}
      onBannerChange={handleBannerChange}
      onSave={handleSave}
      onCancel={() => navigate('/mi_perfil')}
    >
      {/* Profile Info Section */}
      <EditSection title="Información Personal">
        <div className="flex items-start gap-8">
          <EditProfileImage
            src={profileImagePreview}
            onChange={handleProfileImageChange}
          />
          
          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                name="nombre"
                value={profileData.nombre}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slogan
              </label>
              <input
                type="text"
                name="slogan"
                value={profileData.slogan}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ubicación
              </label>
              <input
                type="text"
                name="ubicacion"
                value={profileData.ubicacion}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </EditSection>

      {/* Social Media Section */}
      <EditSection title="Redes Sociales">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-4">
            <Facebook className="w-6 h-6 text-blue-500" />
            <input
              type="text"
              name="facebook"
              value={profileData.facebook}
              onChange={handleInputChange}
              placeholder="Usuario de Facebook"
              className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-4">
            <Instagram className="w-6 h-6 text-pink-500" />
            <input
              type="text"
              name="instagram"
              value={profileData.instagram}
              onChange={handleInputChange}
              placeholder="Usuario de Instagram"
              className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </EditSection>

      {/* Experience Section */}
      <EditSection title="Experiencia">
        <textarea
          name="experiencia"
          value={profileData.experiencia}
          onChange={handleInputChange}
          placeholder="Describe tu experiencia en el sector inmobiliario..."
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[150px]"
        />
      </EditSection>

      {/* About Section */}
      <EditSection title="Sobre Nosotros">
        <textarea
          name="sobre_nosotros"
          value={profileData.sobre_nosotros || ''}
          onChange={handleInputChange}
          placeholder="Cuéntanos más sobre ti..."
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[150px]"
        />
        <p className="text-sm text-gray-500 mt-2">
          Esta información aparecerá en tu perfil público.
        </p>
      </EditSection>
    </BaseEditProfile>
  );
};

export default EditSellerProfile;